import styled from '@emotion/styled';
import { Wws_WeddingWebsiteV2 } from '../../types/graphql';
import BaseNextImage from '../NextImage/BaseNextImage';
import DynamicNextImage from '../NextImage/DynamicNextImage';
import InlineEditHandler from '../InlineEdit/InlineEditHandler/InlineEditHandler';
import { buildEditCoverPhotoContextMenuButtons } from '../InlineEdit/InlineEditMessageFactory';

const ImageWrapper = styled('div')`
  display: flex;
  height: 100vh;
  position: sticky;
  top: 0;
  width: 60%;

  @media (max-width: 1024px) {
    padding-top: 56px; // A spacer for the mobile nav
    position: initial;
    width: 100%;
    height: auto;
  }
`;

const ImageContainer = styled('div')`
  display: flex;
  flex: 1;
  position: relative;
`;

const MobileImageWrapper = styled('div')`
  @media (min-width: 1025px) {
    display: none;
  }

  flex: 1;
`;

const DesktopImageWrapper = styled('div')`
  display: none;

  @media (min-width: 1025px) {
    display: flex;
    flex: 1;
  }
`;

const StyledDesktopInlineEditHandler = styled(InlineEditHandler)`
  display: flex;
  flex: 1;
`;

interface SinglePageCoverImageProps {
  weddingWebsiteResult?: Wws_WeddingWebsiteV2;
  coverImagePath?: string;
  children?: React.ReactNode;
}

const SinglePageCoverImage = ({
  weddingWebsiteResult,
  coverImagePath,
  children,
}: SinglePageCoverImageProps) => {
  if (!weddingWebsiteResult || !coverImagePath) {
    return null;
  }

  const { firstName, fianceFirstName, coverPhoto } = weddingWebsiteResult;

  const alt =
    firstName && fianceFirstName
      ? `${firstName} & ${fianceFirstName} Home Page Banner`
      : 'Home Page Banner';

  const crop = coverPhoto?.cropBox;
  const parsedRotation = Number(coverPhoto?.rotationAngle);
  const coverPhotoRotation = Number.isFinite(parsedRotation)
    ? parsedRotation
    : 0;

  return (
    <ImageWrapper>
      <ImageContainer>
        <DesktopImageWrapper>
          <StyledDesktopInlineEditHandler
            contextMenuButtons={buildEditCoverPhotoContextMenuButtons()}
            outlineOffset={-2}
            contextMenuPosition="inside"
          >
            <BaseNextImage
              src={coverImagePath}
              alt={alt}
              dataTestId="single-page-image"
              imageFit="crop"
              loading="eager"
              priority
              fill
              crop={crop || ''}
              rotation={coverPhotoRotation}
              quality={90}
              sizes="(min-width: 1025px) 60vw, 0vw"
            />
          </StyledDesktopInlineEditHandler>
        </DesktopImageWrapper>
        <MobileImageWrapper>
          <InlineEditHandler
            contextMenuButtons={buildEditCoverPhotoContextMenuButtons()}
            outlineOffset={-2}
          >
            <DynamicNextImage
              src={coverImagePath}
              alt={alt}
              dataTestId="single-page-image-mobile"
              imageFit="crop"
              loading="eager"
              priority
              width={coverPhoto?.width || undefined}
              height={coverPhoto?.height || undefined}
              crop={crop || ''}
              rotation={coverPhotoRotation}
              quality={90}
              sizes="(max-width: 1024px) 100vw, 0vw"
            />
          </InlineEditHandler>
        </MobileImageWrapper>
        {children}
      </ImageContainer>
    </ImageWrapper>
  );
};

export default SinglePageCoverImage;
